/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@reactive-resume/ui";
import { motion } from "framer-motion";

const faqs = [
  {
    q: "How much does CareerBldr cost?",
    a: "CareerBldr offers four plans starting at $9/month for the Starter tier. The Pro plan at $19/month is our most popular and includes full AI writing, ATS scoring, job description matching, cover letters, and DOCX export. Save up to 5 months with annual billing. Every new subscription includes a 3-day free trial, and all plans come with a 30-day money-back guarantee.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes. New subscribers get a 3-day free trial. We collect a payment method at checkout so the plan continues automatically if you don't cancel. You can cancel anytime from account settings before the trial ends and you will not be charged.",
  },
  {
    q: "I already created a resume when CareerBldr was free. What happens to it?",
    a: "You can still open, download, and export any resume you created before paid plans returned. Creating new resumes, editing, AI tools, ATS scoring, and other product features require a subscription.",
  },
  {
    q: "Can I cancel my subscription at any time?",
    a: "Yes. You can cancel anytime from your account settings. It takes you directly to the Stripe billing portal where you manage your subscription. Your access continues until the end of your current billing period.",
  },
  {
    q: "How does the AI resume writing work?",
    a: "CareerBldr uses the latest AI models to generate, improve, and tailor resume content. You can generate bullet points from your job title, improve existing text with one click, quantify achievements, change tone, and even have AI write entire sections. All content is original and specific to your experience, not selected from a pre-written phrase library.",
  },
  {
    q: "What is ATS and why does it matter?",
    a: "ATS (Applicant Tracking System) is software used by 85%+ of companies to screen resumes before a human ever sees them. If your resume doesn't parse correctly or doesn't contain the right keywords, it gets filtered out automatically. CareerBldr's templates are all ATS-verified, and the Pro plan includes real-time ATS scoring to ensure your resume passes.",
  },
  {
    q: "Can I export my resume as PDF and Word/DOCX?",
    a: "PDF export is available on all plans. DOCX (Word) export is available on Pro and above. Both formats are optimized for ATS parsing. If you created a resume before paid plans returned, you can still export that resume as PDF or DOCX.",
  },
  {
    q: "How does job description matching work?",
    a: "Paste any job posting into CareerBldr and the AI instantly analyzes it against your resume. It identifies missing keywords, highlights skill gaps, and suggests specific improvements. On the Career Max plan, auto-tailoring adjusts your resume content to match the job description automatically.",
  },
  {
    q: "Is my data secure?",
    a: "Your data is stored securely and encrypted. We use Stripe for all payment processing, so we never see or store your credit card details. You can delete your account and all associated data at any time from your settings.",
  },
  {
    q: "Who is behind CareerBldr?",
    a: "CareerBldr is built by Studio Algorithm, a mission-driven organization focused on making AI career tools accessible. A portion of proceeds supports our mission of accessible career development through education programs.",
  },
];

export const FAQSection = () => (
  <section id="faq" className="container relative py-24 sm:py-32">
    <div className="grid gap-12 lg:grid-cols-3">
      <motion.div
        className="space-y-4"
        viewport={{ once: true }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">
          {t`FAQ`}
        </p>
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {t`Frequently asked questions`}
        </h2>
        <p className="text-lg opacity-70">
          {t`Everything you need to know about building your resume with CareerBldr.`}
        </p>
      </motion.div>

      <motion.div
        className="lg:col-span-2"
        viewport={{ once: true }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left text-base font-medium">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-foreground/70">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </div>
  </section>
);
